// @flow
import { fetchSetting, setSettingFetchingStatus } from '../../../store/settings/actions';
import { getPromoCampaign } from './campaigns';
import { PLUS_CHECKOUT_PATH } from './constants';

// Lo que como máximo se espera al ajuste antes de servir la página sin él.
const PRELOAD_DEADLINE_MS = 1500;

/**
 * Resuelve el ajuste de una campaña en el servidor, cuando la petición es para su URL.
 *
 * Con esto el estado inicial que viaja al navegador ya trae la respuesta, y el primer render del
 * cliente sabe si la campaña está viva sin tener que pedir nada: una campaña apagada no se pinta
 * ni cuenta impresiones, y no hay un cambio de página después de hidratar.
 *
 * Lo llama App.loadInitialData, que es el único gancho de SSR que se ejecuta de verdad en esta
 * ruta (el de ProductSubscriptionContainer lo pierde la cadena de HOCs). En una navegación dentro
 * de la SPA no pasa por aquí y lo pide usePromoStatus al montar.
 *
 * Se comprueba también la ruta: el parámetro por sí solo aparecería en cualquier URL del sitio que
 * lo arrastre, y no tiene sentido pedir el ajuste para una ficha de fondo.
 *
 * NUNCA rechaza, y eso es lo importante: esta promesa entra en el Promise.all de App.loadInitialData,
 * y el ssr() de front-core convierte cualquier rechazo de ahí en un 500 con plantilla de error. Un
 * 4xx/5xx del endpoint sí lo absorbe el onError de finectAPI, pero un host inalcanzable, un
 * content-type que no sea JSON o un 502 sin content-type relanzan (finectAPI solo llama a onError si
 * el error trae `status`). Un ajuste de marketing no puede tumbar la página de compra: si falla, se
 * sigue sin él y lo pide el navegador al montar.
 *
 * El dispatch va dentro del `then` y no como argumento de `Promise.resolve` para que un throw
 * síncrono —por ejemplo un `fetch` que no exista en ese entorno— también acabe en el `catch` en vez
 * de escaparse antes de que exista la promesa.
 *
 * Cuando no se puede resolver —error o tardanza— el ajuste se deja marcado como NO en vuelo, y de eso
 * depende que el navegador lo reintente: fetchSetting apunta `fetching: true` antes de pedirlo, y si
 * ese `fetching` viajara puesto en el estado inicial, usePromoStatus lo leería como «hay una petición
 * en marcha» y no volvería a pedirlo nunca. El interruptor de apagado se quedaría sin efecto en esa
 * carga y la landing no contaría ni impresiones ni atribución.
 *
 * Y hay plazo: ni finectAPI ni el ssr() de front-core tienen timeout, y el fetch de Node espera
 * minutos, así que un endpoint que acepte la conexión y no contese retendría el worker de SSR con la
 * página de compra sin responder. Este ajuste es opcional —el navegador lo pide si falta—, así que
 * pasado el plazo se sigue sin él.
 *
 * @param store    Store de redux
 * @param params   Parámetros de la query de la petición
 * @param location Ruta de la petición
 */
export const preloadPromoSetting = (store: Object, params: Object = {}, location: string = ''): Promise<any> => {
  const campaign = getPromoCampaign(params);

  if (!campaign || !location.startsWith(PLUS_CHECKOUT_PATH)) return Promise.resolve(null);

  const { setting } = campaign;

  const giveUp = () =>
    Promise.resolve()
      .then(() => store.dispatch(setSettingFetchingStatus(setting, false)))
      .catch(() => null);

  const request = Promise.resolve()
    .then(() => store.dispatch(fetchSetting(setting)))
    .catch(giveUp);

  return new Promise((resolve) => {
    const deadline = setTimeout(() => resolve(giveUp()), PRELOAD_DEADLINE_MS);

    request.then(() => {
      clearTimeout(deadline);
      resolve(null);
    });
  });
};
